import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, RefreshCw01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useConnectors } from "@/lib/connectors";
import { executeSoapTool, useSoapHealth, type SoapExecuteResponse } from "@/lib/soapTools";
import { SOAP_GROUPS, buildSoapBody, type SoapFieldSpec, type SoapGroup, type SoapOperationSpec } from "./soapTools/catalog";
import { Field, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";

/**
 * SOAP Tools — request builder pro 9 eCobra SOAP operací s náhledem XML
 * požadavku a health checkem. Bez connector pickeru (jediný SOAP endpoint).
 * Port legacy `templates/admin/soap-tools.html` + `soap-tools.js`.
 */
export function SoapToolsPage() {
  const { t } = useTranslation();
  const { data: connectors, isLoading } = useConnectors();
  const soap = connectors?.find((c) => c.connectorType === "ECOBRA_SOAP");
  const endpoint = (soap?.config?.endpoint as string | undefined) ?? "";
  const health = useSoapHealth(!!soap);

  const [groupKey, setGroupKey] = useState<SoapGroup>("otp");
  const group = SOAP_GROUPS.find((g) => g.group === groupKey) ?? SOAP_GROUPS[0];
  const [opId, setOpId] = useState(group.operations[0].id);
  const op = group.operations.find((o) => o.id === opId) ?? group.operations[0];

  const selectGroup = (gk: SoapGroup) => {
    const g = SOAP_GROUPS.find((x) => x.group === gk)!;
    setGroupKey(gk);
    setOpId(g.operations[0].id);
  };

  if (isLoading) return <LoadingIndicator />;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("soaptools.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("soaptools.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {!soap ? (
        <div className="flex items-center justify-center rounded-xl bg-primary p-8 text-sm text-tertiary ring-1 ring-secondary">
          {t("soaptools.noConnector")}
        </div>
      ) : (
        <>
          {/* Endpoint + health */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-primary p-4 ring-1 ring-secondary">
            <span className="text-sm text-tertiary">{t("soaptools.endpoint")}:</span>
            <span className="min-w-0 flex-1 truncate font-mono text-sm text-secondary">{endpoint || "—"}</span>
            <Badge type="pill-color" size="sm" color={health.isFetching ? "gray" : health.data?.healthy ? "success" : "error"}>
              {health.isFetching
                ? t("soaptools.health.checking")
                : health.data?.healthy
                  ? t("soaptools.health.healthy")
                  : t("soaptools.health.unhealthy")}
            </Badge>
            <Button size="sm" color="secondary" iconLeading={RefreshCw01} isDisabled={health.isFetching} onPress={() => void health.refetch()}>
              {t("soaptools.health.refresh")}
            </Button>
          </div>

          {/* Group tabs */}
          <div className="flex flex-wrap gap-1">
            {SOAP_GROUPS.map((g) => (
              <button
                key={g.group}
                type="button"
                onClick={() => selectGroup(g.group)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                  groupKey === g.group ? "bg-secondary text-primary ring-1 ring-secondary" : "text-secondary hover:bg-primary_hover"
                }`}
              >
                {t(g.label)}
              </button>
            ))}
          </div>

          {/* Operation sub-tabs */}
          <div className="flex flex-wrap gap-1 border-t border-secondary pt-2">
            {group.operations.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setOpId(o.id)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  opId === o.id ? "bg-brand-solid text-white" : "text-secondary hover:bg-primary_hover"
                }`}
              >
                {t(o.label)}
              </button>
            ))}
          </div>

          <OperationForm key={op.id} op={op} />
        </>
      )}
    </div>
  );
}

function OperationForm({ op }: { op: SoapOperationSpec }) {
  const { t } = useTranslation();

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const f of op.fields) if (f.default !== undefined) v[f.name] = f.default;
    return v;
  });
  const setVal = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }));

  const [result, setResult] = useState<SoapExecuteResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const run = async () => {
    setValidationError(null);
    for (const f of op.fields) {
      if (f.required && !String(values[f.name] ?? "").trim()) {
        setValidationError(t("apitools.required", { field: t(f.label) }));
        return;
      }
    }

    setRunning(true);
    setResult(null);
    try {
      setResult(await executeSoapTool(op.id, buildSoapBody(op, values)));
    } catch (e) {
      setResult({ success: false, error: (e as Error).message, securityBlock: false, rawStatus: 0 });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Formulář */}
      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t(op.label)}</h2>

        {op.fields.map((f) => (
          <SoapFieldInput key={f.name} field={f} value={values[f.name]} onChange={(v) => setVal(f.name, v)} />
        ))}

        {validationError && <p className="text-sm text-error-primary">{validationError}</p>}

        <div>
          <Button size="lg" iconLeading={Play} isDisabled={running} onPress={() => void run()}>
            {running ? t("testing.running") : t("soaptools.execute")}
          </Button>
        </div>
      </div>

      {/* Response */}
      <SoapResultViewer result={result} />
    </div>
  );
}

function SoapFieldInput({ field, value, onChange }: { field: SoapFieldSpec; value: unknown; onChange: (v: unknown) => void }) {
  const { t } = useTranslation();
  const label = `${t(field.label)}${field.required ? " *" : ""}`;

  switch (field.kind) {
    case "textarea":
      return (
        <Field label={label}>
          <textarea
            className={`${inputCls} h-24`}
            value={String(value ?? "")}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );
    case "number":
      return (
        <Field label={label}>
          <input
            className={inputCls}
            type="number"
            value={String(value ?? "")}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );
    case "select":
      return (
        <Field label={label}>
          <AppSelect value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label.includes(".") ? t(o.label) : o.label}
              </option>
            ))}
          </AppSelect>
        </Field>
      );
    default:
      // text | csv
      return (
        <Field label={label}>
          <input
            className={inputCls}
            value={String(value ?? "")}
            placeholder={field.placeholder ?? (field.kind === "csv" ? "a, b, c" : undefined)}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      );
  }
}

function SoapResultViewer({ result }: { result: SoapExecuteResponse | null }) {
  const { t } = useTranslation();

  if (!result) {
    return (
      <div className="flex items-center justify-center rounded-xl bg-primary p-5 text-sm text-tertiary ring-1 ring-secondary">
        {t("soaptools.executeHint")}
      </div>
    );
  }

  if (result.securityBlock) {
    return (
      <div className="flex flex-col gap-2 rounded-xl bg-primary p-5 ring-1 ring-error">
        <h2 className="text-md font-semibold text-error-primary">{t("apitools.sessionStale.title")}</h2>
        <p className="text-sm text-secondary">{t("apitools.sessionStale.body")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <Badge type="pill-color" size="sm" color={result.success ? "success" : "error"}>
        {result.success ? t("apitools.success") : t("apitools.error")}
      </Badge>

      {result.requestXml && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("soaptools.requestXml")}</h4>
          <pre className="max-h-72 overflow-auto rounded-md bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
            {formatXml(result.requestXml)}
          </pre>
        </div>
      )}

      {result.success ? (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("soaptools.response")}</h4>
          <pre className="max-h-96 overflow-auto rounded-md bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
            {JSON.stringify(result.result, null, 2)}
          </pre>
        </div>
      ) : (
        <div className="rounded-lg bg-secondary p-3 text-sm ring-1 ring-error">
          <span className="text-error-primary">{result.error || t("common.loadError")}</span>
        </div>
      )}
    </div>
  );
}

/** Naivní XML pretty-print (odsazení 2 mezery) — jako legacy `formatXml`. */
function formatXml(xml: string): string {
  const reg = /(>)(<)(\/*)/g;
  const nodes = xml.replace(reg, "$1\n$2$3").split("\n");
  let pad = 0;
  let out = "";
  for (const node of nodes) {
    let indent = 0;
    if (/^<\/\w/.test(node)) {
      if (pad > 0) pad -= 1;
    } else if (/^<\w[^>]*[^/]>.*$/.test(node) && !/<\/\w/.test(node)) {
      indent = 1;
    }
    out += "  ".repeat(pad) + node + "\n";
    pad += indent;
  }
  return out.trim();
}
